/**
 * Single Sign-On (OIDC / Keycloak) configuration — Owner-only.
 *
 * The client secret is write-only: the API never returns it (only
 * ``has_client_secret``), so the field stays blank and a blank submit keeps the
 * stored value. Functional form; visual polish is a later refinement.
 */

import { useEffect, useState } from "react";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Space,
  Switch,
  Tag,
  Typography,
  Upload,
} from "antd";

import {
  ApiError,
  type OidcConfigData,
  type OidcConfigPatch,
  type OidcTestResult,
  api,
} from "../../api/client";

interface OidcFormValues {
  enabled: boolean;
  issuer: string;
  client_id: string;
  client_secret: string;
  confidential: boolean;
  scopes: string;
  username_claim: string;
  email_claim: string;
  role_claim: string;
  role_map: string;
  session_ttl_days: number;
  rp_logout_enabled: boolean;
}

export default function OidcSettingsPage() {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm<OidcFormValues>();
  const [cfg, setCfg] = useState<OidcConfigData | null>(null);
  const [saving, setSaving] = useState(false);
  const confidential = Form.useWatch("confidential", form);
  const isPublic = confidential === false;
  const [testing, setTesting] = useState(false);
  const [loginTesting, setLoginTesting] = useState(false);
  const [testUser, setTestUser] = useState("");
  const [testPass, setTestPass] = useState("");
  const [testResult, setTestResult] = useState<OidcTestResult | null>(null);

  const apply = (c: OidcConfigData) => {
    setCfg(c);
    form.setFieldsValue({
      enabled: c.enabled,
      issuer: c.issuer ?? "",
      client_id: c.client_id ?? "",
      client_secret: "",
      confidential: c.confidential,
      scopes: c.scopes,
      username_claim: c.username_claim,
      email_claim: c.email_claim,
      role_claim: c.role_claim ?? "",
      role_map: JSON.stringify(c.role_map ?? {}, null, 2),
      session_ttl_days: c.session_ttl_days,
      rp_logout_enabled: c.rp_logout_enabled,
    });
  };

  useEffect(() => {
    api
      .getOidcConfig()
      .then(apply)
      .catch((e: unknown) =>
        message.error(
          e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e),
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    let v: OidcFormValues;
    try {
      v = await form.validateFields();
    } catch {
      return; // AntD highlights the invalid fields; nothing else to do.
    }
    let roleMap: Record<string, string> = {};
    if (v.role_map.trim()) {
      try {
        roleMap = JSON.parse(v.role_map) as Record<string, string>;
      } catch {
        message.error("Role map is not valid JSON");
        return;
      }
    }
    const payload: OidcConfigPatch = {
      enabled: v.enabled,
      issuer: v.issuer,
      client_id: v.client_id,
      confidential: v.confidential,
      scopes: v.scopes,
      username_claim: v.username_claim,
      email_claim: v.email_claim,
      role_claim: v.role_claim,
      role_map: roleMap,
      session_ttl_days: v.session_ttl_days,
      rp_logout_enabled: v.rp_logout_enabled,
    };
    if (v.client_secret) payload.client_secret = v.client_secret;
    setSaving(true);
    try {
      apply(await api.patchOidcConfig(payload));
      message.success("Saved");
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (creds?: { username: string; password: string }) => {
    const setBusy = creds ? setLoginTesting : setTesting;
    setBusy(true);
    try {
      const r = await api.testOidcConnection(creds);
      setTestResult(r);
      if (!r.ok) {
        message.error(`Connection failed: ${r.error ?? "unknown"}`);
      } else if (r.login_attempted) {
        if (r.login_ok) message.success("Login authorized");
        else message.error(`Login not authorized: ${r.login_error ?? "unknown"}`);
      } else {
        message.success("Connection OK");
      }
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Import a Keycloak JSON, filling whatever it carries. Frontend-only, accepts
  // EITHER:
  //  - the realm's .well-known/openid-configuration → fills `issuer` (every
  //    endpoint is derived from it via runtime discovery); it has NO client
  //    credentials, OR
  //  - a Keycloak client export/representation → fills `client_id` from
  //    `clientId`, and `client_secret` from `secret` if the export includes it.
  const onImportJson = (file: File): boolean => {
    void file.text().then((text) => {
      let doc: Record<string, unknown>;
      try {
        doc = JSON.parse(text) as Record<string, unknown>;
      } catch {
        message.error("Could not parse the JSON file");
        return;
      }
      const filled: string[] = [];
      // Discovery document.
      if (typeof doc.issuer === "string" && typeof doc.jwks_uri === "string") {
        form.setFieldValue("issuer", doc.issuer);
        filled.push("Issuer");
      }
      // Keycloak client representation / export.
      if (typeof doc.clientId === "string") {
        form.setFieldValue("client_id", doc.clientId);
        filled.push("Client ID");
      }
      if (typeof doc.secret === "string" && doc.secret) {
        form.setFieldValue("client_secret", doc.secret);
        filled.push("Client secret");
      }
      if (filled.length === 0) {
        message.error(
          "Unrecognised JSON. Drop the realm's .well-known/openid-configuration " +
            "(fills Issuer) or a Keycloak client export (fills Client ID / secret).",
        );
        return;
      }
      // Tell them what's still missing, based on the current form state.
      const haveIssuer = filled.includes("Issuer") || !!form.getFieldValue("issuer");
      const haveClient =
        filled.includes("Client ID") || !!form.getFieldValue("client_id");
      const todo: string[] = [];
      if (!haveIssuer) todo.push("Issuer (import the .well-known doc)");
      if (!haveClient) todo.push("Client ID (from your Keycloak client)");
      const tail = todo.length ? ` Still needed: ${todo.join("; ")}.` : "";
      message.success(`Imported: ${filled.join(", ")}.${tail}`);
    });
    return false; // never actually upload — we parse it in the browser
  };

  const secretLabel = cfg?.has_client_secret
    ? "Client secret (set — leave blank to keep)"
    : "Client secret";

  return (
    <Card
      title="Single Sign-On (OIDC / Keycloak)"
      extra={
        cfg ? (
          <Tag color={cfg.enabled ? "green" : "default"}>
            {cfg.enabled ? "enabled" : "disabled"}
          </Tag>
        ) : null
      }
    >
      <Form form={form} layout="vertical" style={{ maxWidth: 680 }}>
        <Form.Item
          label="Import from Keycloak"
          tooltip="Drop the realm's .well-known/openid-configuration (fills Issuer), or a Keycloak client export (fills Client ID + secret). The discovery doc has no client credentials; the client export may omit the secret."
        >
          <Upload
            accept=".json,application/json"
            maxCount={1}
            showUploadList={false}
            beforeUpload={onImportJson}
          >
            <Button>Import JSON…</Button>
          </Upload>
        </Form.Item>
        <Form.Item
          name="issuer"
          label="Issuer URL"
          tooltip="e.g. https://auth.palapian.org/realms/Wanderzirkus"
        >
          <Input placeholder="https://auth.example.org/realms/your-realm" />
        </Form.Item>
        <Form.Item name="client_id" label="Client ID">
          <Input placeholder="strichliste-admin" />
        </Form.Item>
        <Form.Item
          name="confidential"
          label="Confidential client"
          valuePropName="checked"
          tooltip="On → the backend authenticates to Keycloak with a client secret (client_secret_basic). Off → public client (PKCE only, no secret) — i.e. Keycloak's 'Client authentication' is off."
        >
          <Switch checkedChildren="confidential" unCheckedChildren="public" />
        </Form.Item>
        {!isPublic && (
          <Form.Item name="client_secret" label={secretLabel}>
            <Input.Password placeholder={cfg?.has_client_secret ? "••••••••" : ""} />
          </Form.Item>
        )}
        <Form.Item name="scopes" label="Scopes">
          <Input />
        </Form.Item>
        <Space size="large" wrap>
          <Form.Item name="username_claim" label="Username claim">
            <Input />
          </Form.Item>
          <Form.Item name="email_claim" label="Email claim">
            <Input />
          </Form.Item>
        </Space>
        <Form.Item
          name="role_claim"
          label="Role claim"
          tooltip="Dotted path, e.g. groups or realm_access.roles. Blank disables auto-role."
        >
          <Input placeholder="(blank — everyone lands pending approval)" />
        </Form.Item>
        <Form.Item
          name="role_map"
          label="Role map — JSON { claim value: role }"
          tooltip='e.g. {"strichliste-office": "office"}'
        >
          <Input.TextArea rows={5} style={{ fontFamily: "monospace" }} />
        </Form.Item>
        <Space size="large" wrap>
          <Form.Item name="session_ttl_days" label="Session TTL (days)">
            <InputNumber min={1} max={365} />
          </Form.Item>
          <Form.Item
            name="rp_logout_enabled"
            label="RP-initiated logout"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item name="enabled" label="Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>
        <Button type="primary" loading={saving} onClick={onSave}>
          Save
        </Button>
      </Form>

      <Divider orientation="left" style={{ marginTop: 8 }}>
        Test
      </Divider>
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Button loading={testing} onClick={() => void runTest()}>
          Test connection
        </Button>
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          Optionally check a real login: enter a Keycloak user's email/username +
          password to see if they're authorized and which role they'd map to. Uses
          the password grant — needs "Direct Access Grants" enabled on the client;
          the password is used once and never stored.
        </Typography.Paragraph>
        <Space.Compact style={{ width: "100%", maxWidth: 520 }}>
          <Input
            placeholder="email or username"
            value={testUser}
            onChange={(e) => setTestUser(e.target.value)}
            autoComplete="off"
          />
          <Input.Password
            placeholder="password"
            value={testPass}
            onChange={(e) => setTestPass(e.target.value)}
            autoComplete="off"
          />
          <Button
            loading={loginTesting}
            disabled={!testUser || !testPass}
            onClick={() => void runTest({ username: testUser, password: testPass })}
          >
            Test login
          </Button>
        </Space.Compact>

        {testResult && (
          <div>
            <Alert
              type={testResult.ok ? "success" : "error"}
              showIcon
              message={testResult.ok ? "Connection OK" : "Connection failed"}
              description={
                testResult.ok ? (
                  <Descriptions size="small" column={1} style={{ marginTop: 8 }}>
                    <Descriptions.Item label="Issuer">
                      {testResult.issuer}
                    </Descriptions.Item>
                    <Descriptions.Item label="Authorization">
                      {testResult.authorization_endpoint}
                    </Descriptions.Item>
                    <Descriptions.Item label="Token">
                      {testResult.token_endpoint}
                    </Descriptions.Item>
                    <Descriptions.Item label="JWKS">
                      {testResult.jwks_uri}
                    </Descriptions.Item>
                    <Descriptions.Item label="Signing keys">
                      {testResult.signing_keys}
                    </Descriptions.Item>
                    <Descriptions.Item label="Scopes">
                      {(testResult.scopes_supported ?? []).join(", ") || "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Redirect URI to register">
                      <code>
                        {testResult.redirect_uri ?? "(set ADMIN_PUBLIC_URL)"}
                      </code>
                    </Descriptions.Item>
                  </Descriptions>
                ) : (
                  <code>{testResult.error}</code>
                )
              }
            />
            {testResult.login_attempted && (
              <Alert
                style={{ marginTop: 12 }}
                type={testResult.login_ok ? "success" : "error"}
                showIcon
                message={
                  testResult.login_ok
                    ? "Login authorized"
                    : "Login not authorized"
                }
                description={
                  testResult.login_ok ? (
                    <Descriptions size="small" column={1} style={{ marginTop: 8 }}>
                      <Descriptions.Item label="Would map to role">
                        {testResult.resolved_role ?? "(none → pending approval)"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Claims">
                        <code style={{ whiteSpace: "pre-wrap" }}>
                          {JSON.stringify(testResult.login_claims ?? {}, null, 2)}
                        </code>
                      </Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <code>{testResult.login_error}</code>
                  )
                }
              />
            )}
          </div>
        )}
      </Space>
    </Card>
  );
}
